import { getBasePath } from "./base-path";
import { http } from "./http";
import type {
  AdminComponent,
  AdminComponentGroup,
  AdminComponentWithGroup,
  AdminIncident,
  AdminWhoAmI,
  BackfillState,
  IncidentDetailResponse,
  IncidentResponse,
  StatusLogEntry,
  SummaryResponse,
  UptimeBucket,
} from "./types";

function apiPath(path: string): string {
  return getBasePath() + path;
}

export const publicApi = {
  summary: () => http<SummaryResponse>(apiPath("/api/v1/summary?ui_only=true")),
  incidents: () => http<{ incidents: IncidentResponse[] }>(apiPath("/api/v1/incidents?ui_only=true")),
  incidentDetail: (id: string) => http<IncidentDetailResponse>(apiPath(`/api/v1/incidents/${id}`)),
  uptime: (range: string, uiOnly = true) =>
    http<Record<string, UptimeBucket[]>>(
      apiPath(`/api/v1/uptime?range=${encodeURIComponent(range)}${uiOnly ? "&ui_only=true" : ""}`),
    ),
  groupUptime: (range: string, uiOnly = true) =>
    http<Record<string, UptimeBucket[]>>(
      apiPath(`/api/v1/group_uptime?range=${encodeURIComponent(range)}${uiOnly ? "&ui_only=true" : ""}`),
    ),
};

type IncidentPayload = {
  name: string;
  status: string;
  impact: string;
  body?: string;
  started_at?: string;
  component_ids: string[];
  components: Record<string, string>;
};

export const adminApi = {
  whoami: () => http<AdminWhoAmI>(apiPath("/api/v1/whoami")),
  incidents: () => http<AdminIncident[]>(apiPath("/api/v1/incidents")),
  incident: (id: string) =>
    http<{ incident: AdminIncident; updates: unknown[] }>(apiPath(`/api/v1/incidents/${id}`)),
  createIncident: (payload: IncidentPayload) =>
    http<AdminIncident>(apiPath("/api/v1/incidents"), { method: "POST", body: JSON.stringify(payload) }),
  updateIncident: (id: string, payload: Partial<IncidentPayload>) =>
    http<AdminIncident>(apiPath(`/api/v1/incidents/${id}`), { method: "PATCH", body: JSON.stringify(payload) }),
  deleteIncident: (id: string) => http<void>(apiPath(`/api/v1/incidents/${id}`), { method: "DELETE" }),
  createIncidentUpdate: (id: string, payload: { status: string; body: string }) =>
    http(apiPath(`/api/v1/incidents/${id}/incident_updates`), {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  deleteIncidentUpdate: (id: string, updateId: string) =>
    http<void>(apiPath(`/api/v1/incidents/${id}/incident_updates/${updateId}`), { method: "DELETE" }),
  components: () => http<AdminComponentWithGroup[]>(apiPath("/api/v1/components")),
  component: (id: string, incidentPage = 1, incidentPerPage = 10) =>
    http<{
      component: AdminComponent;
      incidents: AdminIncident[];
      incidents_total: number;
      incidents_page: number;
      incidents_per_page: number;
    }>(
      apiPath(
        `/api/v1/components/${id}?incident_page=${incidentPage}&incident_per_page=${incidentPerPage}`,
      ),
    ),
  updateComponent: (id: string, payload: { status?: string; monitoring_since?: string }) =>
    http<AdminComponent>(apiPath(`/api/v1/components/${id}`), {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  groups: () => http<AdminComponentGroup[]>(apiPath("/api/v1/component_groups")),
  statusLog: (id: string, page = 1, perPage = 20) =>
    http<{ entries: StatusLogEntry[]; total: number; page: number; per_page: number }>(
      apiPath(`/api/v1/components/${id}/status_log?page=${page}&per_page=${perPage}`),
    ),
  createStatusLog: (id: string, payload: { status: string; changed_at: string }) =>
    http(apiPath(`/api/v1/components/${id}/status_log`), {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateStatusLog: (logId: number, payload: { status?: string; changed_at?: string }) =>
    http<StatusLogEntry>(apiPath(`/api/v1/status_log/${logId}`), {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  deleteStatusLog: (logId: number) =>
    http<void>(apiPath(`/api/v1/status_log/${logId}`), { method: "DELETE" }),
  backfillStatus: () => http<BackfillState[]>(apiPath("/api/v1/backfill/status")),
  backfillComponent: (id: string) =>
    http(apiPath(`/api/v1/backfill/component/${id}`), { method: "POST" }),
  backfillGroup: (id: string) => http(apiPath(`/api/v1/backfill/group/${id}`), { method: "POST" }),
  uptime: (range: string, uiOnly = false) =>
    http<Record<string, UptimeBucket[]>>(
      apiPath(
        `/api/v1/uptime?range=${encodeURIComponent(range)}${uiOnly ? "&ui_only=true" : ""}`,
      ),
    ),
  groupUptime: (range: string, uiOnly = false) =>
    http<Record<string, UptimeBucket[]>>(
      apiPath(
        `/api/v1/group_uptime?range=${encodeURIComponent(range)}${uiOnly ? "&ui_only=true" : ""}`,
      ),
    ),
  componentUptime: (id: string, range: string) =>
    http<UptimeBucket[]>(
      apiPath(
        `/api/v1/components/${encodeURIComponent(id)}/uptime?range=${encodeURIComponent(range)}&ui_only=true`,
      ),
    ),
};
